import { useEffect, useState } from "react";
import { Clock, X, Briefcase } from "lucide-react";
import { getZonedParts, availabilityFromHour } from "../../lib/timezones";
import { countryToTimezone, resolveLeadTimezone } from "../../lib/countryTimezones";
import { useLeadFocus } from "../../context/LeadFocusContext";

/**
 * Compact persistent clock widget for the CRM top bar.
 * Always shows Amsterdam (Gerencia). If `agentTz` prop is provided, also shows
 * the agent's local time. If the user has focused a lead recently, also shows
 * the lead's local time (dismissible).
 */
export default function TopClockBar({ agentTz, agentCity, leadLocations = [] }) {
  const [now, setNow] = useState(new Date());
  const { focusedLead, clearLead } = useLeadFocus();

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const ams = getZonedParts("Europe/Amsterdam", now);
  const amsAvail = ams ? availabilityFromHour(ams.hour) : null;
  const agent = agentTz ? getZonedParts(agentTz, now) : null;
  const agentAvail = agent ? availabilityFromHour(agent.hour) : null;

  // Build dedup'd country/city pills from the agent's assigned leads.
  // Each lead-location is resolved via country first, then city, then city_area.
  const seenTzs = new Set();
  if (agentTz) seenTzs.add(agentTz);
  seenTzs.add("Europe/Amsterdam"); // already shown as Gerencia
  const countryClocks = [];
  for (const loc of leadLocations) {
    const info = resolveLeadTimezone(loc);
    if (!info) continue;
    if (seenTzs.has(info.tz)) continue;
    seenTzs.add(info.tz);
    const parts = getZonedParts(info.tz, now);
    if (!parts) continue;
    const avail = availabilityFromHour(parts.hour);
    // Label: prefer country name, else city, else mapped city from the resolver
    const label = (loc.country || loc.city || loc.city_area || info.city || "").toUpperCase();
    countryClocks.push({
      key: info.tz,
      label,
      time: parts.time,
      color: avail?.color,
      title: avail?.label_es,
    });
  }

  // Lead clock: derive tz from explicit override, country mapping, or city fallback.
  let leadTz = null;
  let leadCity = null;
  if (focusedLead) {
    if (focusedLead.timezone) {
      leadTz = focusedLead.timezone;
    } else {
      const info = focusedLead.country
        ? countryToTimezone(focusedLead.country)
        : resolveLeadTimezone({ city: focusedLead.city, city_area: focusedLead.city_area });
      if (info) {
        leadTz = info.tz;
        leadCity = info.city;
      }
    }
  }
  const lead = leadTz ? getZonedParts(leadTz, now) : null;
  const leadAvail = lead ? availabilityFromHour(lead.hour) : null;

  return (
    <div className="flex items-center gap-2" data-testid="top-clock-bar">
      {/* Amsterdam (always) */}
      <ClockPill
        testid="ams-clock"
        label={`${ams ? "Amsterdam · Gerencia" : "Amsterdam"}`}
        time={ams?.time}
        color={amsAvail?.color}
        title={amsAvail?.label_es}
      />

      {/* Agent local (only if linked) */}
      {agent && (
        <ClockPill
          testid="agent-clock"
          label={agentCity || "Tu zona"}
          time={agent.time}
          color={agentAvail?.color}
          title={agentAvail?.label_es}
          hideOnMobile
        />
      )}

      {/* Lead-country clocks (auto-derived from the agent's assigned leads) */}
      {countryClocks.map((c) => (
        <ClockPill
          key={c.key}
          testid={`country-clock-${c.key}`}
          label={c.label}
          time={c.time}
          color={c.color}
          title={c.title}
          hideOnMobile
        />
      ))}

      {/* Focused lead clock */}
      {lead && focusedLead && (
        <div
          data-testid="lead-clock"
          className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/30"
          title={`Hora local de ${focusedLead.company_name}${leadAvail ? " · " + leadAvail.label_es : ""}`}
        >
          <Briefcase className="w-3 h-3 text-purple-300" />
          <span
            className="w-2 h-2 rounded-full"
            style={{ background: leadAvail?.color || "#64748b" }}
          />
          <div className="leading-tight">
            <div className="text-[9px] uppercase tracking-[0.15em] text-purple-300 mono truncate max-w-[120px]">
              {focusedLead.company_name || leadCity || "Lead"}
            </div>
            <div className="text-sm font-bold text-white mono">{lead.time}</div>
          </div>
          <button
            onClick={clearLead}
            data-testid="lead-clock-clear"
            className="text-white/40 hover:text-white ml-1"
            aria-label="Quitar lead"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

function ClockPill({ testid, label, time, color, title, hideOnMobile }) {
  return (
    <div
      data-testid={testid}
      className={`${hideOnMobile ? "hidden sm:flex" : "flex"} items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/8`}
    >
      <Clock className="w-3.5 h-3.5 text-white/50" />
      <div className="flex items-center gap-2">
        <span
          className="w-2 h-2 rounded-full"
          style={{ background: color || "#64748b" }}
          title={title || ""}
        />
        <div className="leading-tight">
          <div className="text-[9px] uppercase tracking-[0.15em] text-white/40 mono">{label}</div>
          <div className="text-sm font-bold text-white mono">{time || "—"}</div>
        </div>
      </div>
    </div>
  );
}
